import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Row } from '@tanstack/react-table';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { SupportRequestDtoIn } from '@/models/SupportRequestDtoIn';
import SupportRequestType from '@/models/common/SupportRequestType';
import AllowedFor from '@/models/common/AllowedFor';
import { getShortRequestId } from '@/helpers/requestIdHelper';
import { getAggregatedStatus, getStatusGroup } from '@/helpers/requestStatus';
import { formatDateShort } from '@/helpers/dateHelper';
import { formatCurrencyShort } from '@/helpers/currencyHelper';
import Checkbox from '@/components/ui/Checkbox/Checkbox';
import OpenInquiryButton from '@/components/Inquiries/OpenInquiryButton';
import Sheet, { SheetItem } from '@/components/ui/Sheet/Sheet';
import {
  CardAction,
  CardAmount,
  CardBody,
  CardIconButton,
  CardMeta,
  CardRowActions,
  CardSelect,
  CardSide,
  CardStatusLine,
  CardTitle,
  ListCard,
} from '@/components/ui/CardList/CardList';

/*
 * ============================================================
 *  כרטיס בקשת תמיכה — תצוגת המסך הצר
 * ============================================================
 *
 * ⚠️ **חזרה למבנה המדויק של נדרים, לא לשלד המאוחד של תנועות/הוראות
 * קבע.** בסבב קודם איחדתי את שלושת הלוחות לאותו שלד שטוח, כי המשתמשת
 * ביקשה שהם "ייראו זהים". בבדיקה חוזרת של js/pages/dashboard.js
 * (TAB_CONFIG['support-requests'].card) מתברר שבקשות תמיכה הן
 * *במכוון* המבנה השונה היחיד מבין הלוחות של נדרים — כרטיס-במיכל
 * (card-list-boxed), **בלי אייקון בכלל**. תנועות הן card-list-flat
 * עם אייקון; בקשות תמיכה הן card-list-boxed בלי אייקון. אלה שני
 * מבנים שונים בכוונה בעיצוב המקורי, לא פער שצריך לסגור. "אותו
 * עיצוב" פירושו כאן נאמנות למקור של כל לוח, לא שלד זהה לשלושתם.
 *
 * ⚠️ **שדות המטא הם בדיוק שלושת השדות של נדרים, לא פחות ולא יותר**:
 *   main.appendChild(CardList.meta([
 *       shortRequestId(request.correlationId),
 *       type === 'OneTime' ? 'חד פעמי' : 'חודשי',
 *       Format.date(request.requestedAt)
 *   ]));
 * שים לב: אין שם "מטרה" בכלל. סבב קודם הוסיף goalLabel וגם הוריד
 * את מספר הבקשה ואת התאריך — שתי סטיות מהמקור, בכיוונים הפוכים.
 * כאן זה חזר בדיוק לשלושת השדות שיש בקוד המקור.
 *
 * ⚠️ **מה שהכרטיס הזה מחזיר.** בטבלה שש עמודות מסומנות
 * hiddenOnMobile — הבחירה, מספר הבקשה, מספר החשבונית, אמצעי
 * התשלום והסטטוס — ובנוסף כפתור הורדת האסמכתא מוסתר במפורש
 * בתנאי `!isMobile` שבתא הפעולות. מספר הבקשה ותאריך ההגשה כבר
 * חוזרים כאן במטא, כמו בנדרים; מספר החשבונית אין לו מקבילה בנדרים
 * (נדרים לא מציג אותו כלל) והוא כן קיים במגירת הפרטים — אין לו
 * צורך לחזור גם על הכרטיס.
 *
 * הורדת האסמכתא ראויה להערה משלה: היא הפעולה היחידה בשני המסכים
 * שהקוד הקיים **מסיר** בנייד ולא רק מקצר. בנדרים היא באמת לא
 * קיימת — ה-WebView שם לא יודע לשמור קובץ, ולכן היא הוחלפה שם
 * בשליחה במייל. הדפדפן של האתר כן יודע, ולכן היא חוזרת לכאן
 * כפעולה בגיליון.
 */

interface SupportRequestCardProps {
  row: Row<SupportRequestDtoIn>;
  isActive: boolean;
  onOpen: (request: SupportRequestDtoIn) => void;
  /** שם הספק/המשלם עבור בקשת הוצאת קרן, שאין לה נתמך. */
  fundExpenseRecipientName: (request: SupportRequestDtoIn) => string;
  menuItems: { icon: string; text: string; onClick: () => void }[];
  /** הורדת האסמכתא. נכנסת לגיליון כשהבקשה זכאית לה. */
  onDownloadReceipt?: () => void;
  isReceiptEligible: boolean;
  /** תיבת הסימון מוצגת רק במצב בחירה מרובה (ר' DataTable). */
  selectionMode?: boolean;
}

const SupportRequestCard = ({
  row,
  isActive,
  onOpen,
  fundExpenseRecipientName,
  menuItems,
  onDownloadReceipt,
  isReceiptEligible,
  selectionMode = false,
}: SupportRequestCardProps) => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const request = row.original;

  const isFundExpense = request.goal?.allowedFor === AllowedFor.Fund;

  /*
   * לבקשת הוצאת קרן אין נתמך. בטבלה מוצג שם הספק (או מי ששילם
   * וזכאי להחזר) עם אייקון שפותח tooltip שאומר איזה מהשניים.
   *
   * ⚠️ ב-tooltip אין טעם במסך מגע — אין ריחוף — ולכן אותה הבחנה
   * נכתבת כאן כתווית בתוך הכותרת עצמה. זו ההתנהגות בנדרים, ומאותו
   * נימוק. המידע לא יורד; הוא רק מפסיק לדרוש עכבר.
   */
  const title = (): string => {
    if (isFundExpense) {
      const details = request.additionalDetails as { isAlreadyPaid?: boolean } | undefined;
      const label = details?.isAlreadyPaid ? t('payer') : t('supplier');
      return `${label}: ${fundExpenseRecipientName(request)}`;
    }
    return `${request.supportedContact?.firstName ?? ''} ${request.supportedContact?.lastName ?? ''}`.trim();
  };

  const status = getAggregatedStatus(request.statuses);
  const statusGroup = getStatusGroup(status);

  const sheetItems: SheetItem[] = menuItems.map((item, index) => ({
    key: `${index}-${item.text}`,
    label: item.text,
    onClick: item.onClick,
    icon: <img src={item.icon} alt="" />,
    // הפעולה ההרסנית מסומנת כדי שתיבדל בגיליון, כמו במקור.
    danger: item.text === t('cancelSupportRequest.menuLabel'),
  }));

  if (isReceiptEligible && onDownloadReceipt) {
    sheetItems.unshift({
      key: 'receipt',
      label: t('downloadTransferReceipt'),
      onClick: onDownloadReceipt,
    });
  }

  return (
    <>
      <ListCard isActive={isActive} onClick={() => onOpen(request)}>
        {/* מזינה את "העברה לחודש הבא" הקבוצתית. נפתחת מכפתור, לא דרך קבע. */}
        {selectionMode && (
          <CardSelect>
            <Checkbox checked={row.getIsSelected()} onCheckedChange={(value) => row.toggleSelected(!!value)} />
          </CardSelect>
        )}

        {/* בלי CardIcon — הכרטיס-במיכל של נדרים אינו נושא אייקון. */}
        <CardBody>
          <CardTitle strong>{title()}</CardTitle>
          <CardMeta
            items={[
              getShortRequestId(request.correlationId),
              request.type === SupportRequestType.Monthly ? t('monthly') : t('oneTime'),
              formatDateShort(request.requestedAt),
            ]}
          />
        </CardBody>

        <CardSide>
          {/*
           * ⚠️ positive={false} גם כשהסכום חיובי, ובכוונה. ירוק בשפה
           * הזאת אומר "כסף שנכנס לקרן"; סכום של בקשת תמיכה הוא סכום
           * מבוקש, לא תנועה שקרתה, ולצבוע אותו בירוק היה קורא לו
           * הכנסה. זה מתועד כך גם במקור.
           */}
          <CardAmount positive={false}>{formatCurrencyShort(request.amount)}</CardAmount>
          <CardStatusLine color={statusGroup?.iconColor || 'var(--icon-status-1)'}>
            {t(statusGroup?.id || status)}
          </CardStatusLine>
        </CardSide>

        <CardRowActions>
          {/* סדר קבוע, צמוד לקצה השמאלי: ⋮ (כשיש) ואז הפנייה, שהיא תמיד
              האייקון הקיצוני. בלי מקום שמור. */}
          {sheetItems.length > 0 && (
            <CardIconButton title={t('actions')} onClick={() => setIsMenuOpen(true)}>
              <MoreVertIcon sx={{ fontSize: 18 }} />
            </CardIconButton>
          )}
          <CardAction>
            <OpenInquiryButton resourceType="SupportRequest" resourceId={request.correlationId} size="small" />
          </CardAction>
        </CardRowActions>
      </ListCard>

      <Sheet open={isMenuOpen} onClose={() => setIsMenuOpen(false)} title={title()} items={sheetItems} />
    </>
  );
};

export default SupportRequestCard;
